// Duration mapping between numeric minutes and the API's .NET TimeSpan "c"
// wire format, plus sleep-session display helpers shared by client-app and
// coach-portal. The day segment only appears at exactly 24h ("1.00:00:00")
// — SQL can't store it as `time`, but the JSON string can.

const MINUTES_PER_DAY = 24 * 60;

/** A single day can hold at most 24h of sleep across all of its sessions. */
export const MAX_DAILY_SLEEP_MINUTES = MINUTES_PER_DAY;

/** 450 -> "07:30:00", 1440 -> "1.00:00:00" */
export function minutesToTimeSpan(minutes: number): string {
  const days = Math.floor(minutes / MINUTES_PER_DAY);
  const rest = minutes % MINUTES_PER_DAY;
  const h = Math.floor(rest / 60);
  const m = rest % 60;
  const pad = (n: number) => n.toString().padStart(2, '0');
  const hms = `${pad(h)}:${pad(m)}:00`;
  return days > 0 ? `${days}.${hms}` : hms;
}

/** "07:30:00" -> 450, "1.00:00:00" -> 1440. Fractional seconds are ignored. */
export function timeSpanToMinutes(timeSpan: string | undefined): number {
  if (!timeSpan) return 0;
  const [dayPart, timePart] =
    timeSpan.includes('.') && timeSpan.indexOf('.') < timeSpan.indexOf(':')
      ? [timeSpan.slice(0, timeSpan.indexOf('.')), timeSpan.slice(timeSpan.indexOf('.') + 1)]
      : ['0', timeSpan];
  const [h = '0', m = '0'] = timePart.split(':');
  return parseInt(dayPart, 10) * MINUTES_PER_DAY + parseInt(h, 10) * 60 + parseInt(m, 10);
}

/**
 * 450 -> "7h 30min", 420 -> "7h", 30 -> "30min", 0 -> "0h". Sub-hour
 * durations omit the "0h" prefix. Unit labels are passed in so callers can
 * supply translated units ("klst"/"min", "č"/"min", …).
 */
export function formatDurationLabel(minutes: number, hourUnit = 'h', minuteUnit = 'min'): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (minutes === 0) return `0${hourUnit}`;
  if (h === 0) return `${m}${minuteUnit}`;
  if (m === 0) return `${h}${hourUnit}`;
  return `${h}${hourUnit} ${m}${minuteUnit}`;
}

/**
 * Separate per-type ordinal counters for two-type item lists — sleep uses it for
 * "Main Sleep 1 / Main Sleep 2" vs "Nap 1 / Nap 2", hydration for water vs
 * electrolytes. `isSecondaryType` splits the list; `total` is the number of
 * items sharing that item's type, so callers can hide the ordinal when a type
 * occurs only once.
 */
export function computePerTypeOrdinals<T>(
  items: T[],
  isSecondaryType: (item: T) => boolean,
): { ordinal: number; total: number }[] {
  const secondaryTotal = items.filter(isSecondaryType).length;
  const primaryTotal = items.length - secondaryTotal;
  let primaryOrdinal = 0;
  let secondaryOrdinal = 0;

  return items.map((item) =>
    isSecondaryType(item)
      ? { ordinal: ++secondaryOrdinal, total: secondaryTotal }
      : { ordinal: ++primaryOrdinal, total: primaryTotal },
  );
}
